import type { CustomImpositionSheetScale } from "./types";

/** Matches calculation-service PrintedSheetCoordinates.UnitsPerSheetDimension. */
export const UM_PER_CM = 10000;
export const UM_PER_MM = 1000;
export const UM_PER_INCH = 10000;

export function cmToUm(cm: number): number {
  return Math.round(cm * UM_PER_CM);
}

export function mmToUm(mm: number): number {
  return Math.round(mm * UM_PER_MM);
}

export function umToMm(um: number): number {
  return um / UM_PER_MM;
}

export function umToCm(um: number): number {
  return um / UM_PER_CM;
}

export function inchToUm(inch: number): number {
  return Math.round(inch * UM_PER_INCH);
}

export function umToInch(um: number): number {
  return um / UM_PER_INCH;
}

export function createSheetScale(
  sheetWidthUm: number,
  sheetLengthUm: number,
  maxWidthPx: number,
  maxHeightPx: number,
): CustomImpositionSheetScale {
  if (sheetWidthUm <= 0 || sheetLengthUm <= 0 || maxWidthPx <= 0 || maxHeightPx <= 0) {
    return { scale: 0, canvasWidth: 0, canvasHeight: 0 };
  }

  const scale = Math.min(maxWidthPx / sheetWidthUm, maxHeightPx / sheetLengthUm);
  return {
    scale,
    canvasWidth: sheetWidthUm * scale,
    canvasHeight: sheetLengthUm * scale,
  };
}

export function umToPx(um: number, scale: number): number {
  return um * scale;
}

export function pxToUm(px: number, scale: number): number {
  if (scale <= 0) {
    return 0;
  }
  return Math.round(px / scale);
}

export function formatSheetSize(
  widthUm: number,
  lengthUm: number,
  imperial = false,
): string {
  if (imperial) {
    return `${trimNumber(umToInch(widthUm))} × ${trimNumber(umToInch(lengthUm))} in`;
  }
  return `${trimNumber(umToMm(widthUm))} × ${trimNumber(umToMm(lengthUm))} mm`;
}

export function formatGap(um: number, imperial = false): string {
  if (imperial) {
    return `${trimNumber(umToInch(um))} in`;
  }
  return `${trimNumber(umToMm(um))} mm`;
}

export function formatDimension(um: number, imperial = false): string {
  if (imperial) {
    return `${trimNumber(umToInch(um))} in`;
  }
  return `${trimNumber(umToMm(um))} mm`;
}

function trimNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");
}
